import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  closeMonthlyReport,
  getFinancialReport,
  getMonthlyReport,
  listMonthlyReportClosings,
  reopenMonthlyReport,
  type MonthlyReportResponse,
} from '../lib/monthlyReportApi';
import { periodKey, type ReportPeriodState } from '../lib/reportPeriod';

// Same 5-minute stale window as the other financial queries; a closed month
// is a frozen snapshot anyway, so refetching it buys nothing.
const FIVE_MINUTES = 5 * 60 * 1000;

export const MONTHLY_REPORT_KEY = ['financials', 'monthly-report'] as const;

export function useMonthlyReport(year: number, month: number, companyIds?: string[]) {
  return useQuery({
    queryKey: [...MONTHLY_REPORT_KEY, year, month, companyIds ?? []],
    queryFn: () => getMonthlyReport(year, month, companyIds),
    staleTime: FIVE_MINUTES,
  });
}

/**
 * Raportul perioadei selectate. Cheia include tipul, cheia perioadei și baza de
 * comparație, ca trecerea de la lună la trimestru să nu servească din cache
 * raportul lunii.
 */
export function useFinancialReport(
  period: ReportPeriodState,
  companyIds?: string[],
  /** False while the picker holds an impossible period (an inverted range). */
  enabled = true,
) {
  return useQuery({
    enabled,
    queryKey: [
      ...MONTHLY_REPORT_KEY,
      'period',
      period.kind,
      periodKey(period),
      period.compare,
      companyIds ?? [],
    ],
    queryFn: () => getFinancialReport(period, companyIds),
    staleTime: FIVE_MINUTES,
  });
}

export function useMonthlyReportClosings(companyIds?: string[]) {
  return useQuery({
    queryKey: [...MONTHLY_REPORT_KEY, 'closings', companyIds ?? []],
    queryFn: () => listMonthlyReportClosings(companyIds),
    staleTime: FIVE_MINUTES,
  });
}

/**
 * Close or reopen a month; the report + closings queries refresh on success.
 * `period` is the state the page is showing, so the freshly computed report
 * lands straight in that query's cache instead of triggering a refetch.
 */
export function useMonthlyReportClosing(
  year: number,
  month: number,
  companyIds?: string[],
  period?: ReportPeriodState,
) {
  const qc = useQueryClient();
  const settle = (data: MonthlyReportResponse) => {
    qc.setQueryData([...MONTHLY_REPORT_KEY, year, month, companyIds ?? []], data);
    if (period) {
      qc.setQueryData(
        [
          ...MONTHLY_REPORT_KEY,
          'period',
          period.kind,
          periodKey(period),
          period.compare,
          companyIds ?? [],
        ],
        data,
      );
    }
    void qc.invalidateQueries({ queryKey: [...MONTHLY_REPORT_KEY, 'closings'] });
  };
  const close = useMutation({
    mutationFn: () => closeMonthlyReport(year, month, companyIds),
    onSuccess: settle,
  });
  const reopen = useMutation({
    mutationFn: () => reopenMonthlyReport(year, month, companyIds),
    onSuccess: settle,
  });
  return { close, reopen };
}
